import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';

import { useFossabotPRCounts } from '../../../hooks/api/fossabot/useFossabotPRCounts';
import List from '../../generic/List';
import SubnavCountBadge from '../../generic/Subnav/SubnavCountBadge';
import type { FossabotPRCounts, FossabotPRSort, FossabotPRVcsState } from '../../../../shared/fossabot/common';

import { FossabotPrsSortMenu } from './FossabotPrsSortMenu';

import styles from './FossabotPrsTableHeader.module.less';

type StateFilter = {
  label: string;
  /** Undefined is the All chip, which the API expresses by sending no state at all. */
  state?: FossabotPRVcsState;
  countKey: keyof FossabotPRCounts;
};

/*
 * All reads `total` rather than adding the four states up. The counts are passed
 * through from the fossabot subgraph and nothing in this repo makes the states a
 * partition of the total, so a sum could disagree with the count on the subnav.
 */
const STATE_FILTERS: StateFilter[] = [
  { label: 'All', countKey: 'total' },
  { label: 'Open', state: 'open', countKey: 'open' },
  { label: 'Closed', state: 'closed', countKey: 'closed' },
  { label: 'Merged', state: 'merged', countKey: 'merged' },
];

/*
 * Hover text on every chip once the counts request has failed, and the one
 * spoken explanation beside them. A row of "--" says nothing on its own, and
 * screen readers routinely skip it, so the failure is spelled out. Matches the
 * wording on the stat cards and the subnav badge.
 */
const UNAVAILABLE_TITLE = 'PR counts unavailable';

/*
 * The chips are focusable, so tabbing through them never reaches the explanation
 * sitting beside them; only a linear browse-mode read does. Describing every chip
 * by it gets it spoken on focus while it stays rendered once.
 */
const UNAVAILABLE_MESSAGE_ID = 'fossabot-pr-counts-unavailable';

type Props = {
  /**
   * The project locator, without the revision ID.
   * @example `custom+17/cool-project`
   */
  projectLocator: string;
  /** The state being filtered on. Undefined is the All chip. */
  state?: FossabotPRVcsState;
  /** Called with the chosen chip's state, or undefined for All. */
  onStateChange: (state?: FossabotPRVcsState) => void;
  /** The order the rows are in, which the sort control reads out. */
  sort: FossabotPRSort;
  /** Called with the order picked from the sort control. */
  onSortChange: (sort: FossabotPRSort) => void;
};

/**
 * The bar above the PR rows, holding one chip per available VCS state plus All
 * on the left and the sort control on the right.
 *
 * The chip counts describe the whole project and are deliberately not narrowed
 * by the active chip, so picking Open leaves the other chips reading what they
 * read before. They are fetched here rather than passed down, matching the stat
 * cards; both go through the same query key, so it stays one request.
 *
 * The two children are pushed apart by the header's own space-between layout,
 * so neither needs any positioning of its own.
 */
export const FossabotPrsTableHeader: VoidFunctionComponent<Props> = ({
  projectLocator,
  state,
  onStateChange,
  sort,
  onSortChange,
}) => {
  const { data: counts, isLoading, isError } = useFossabotPRCounts(projectLocator);

  return (
    <List.Header>
      <div className={styles.chips} role="group" aria-label="Filter pull requests by state">
        {STATE_FILTERS.map((filter) => (
          <button
            type="button"
            key={filter.label}
            className={cx(styles.chip, { [styles.chipActive]: state === filter.state })}
            aria-pressed={state === filter.state}
            aria-describedby={isError ? UNAVAILABLE_MESSAGE_ID : undefined}
            onClick={() => onStateChange(filter.state)}
            data-testid={`fossabot-pr-state-chip-${filter.countKey}`}
          >
            {filter.label}
            <SubnavCountBadge
              count={counts?.[filter.countKey] ?? 0}
              isLoading={isLoading}
              isUnavailable={isError}
              title={isError ? UNAVAILABLE_TITLE : undefined}
            />
          </button>
        ))}
        {isError && <span className="sr-only" id={UNAVAILABLE_MESSAGE_ID}>{UNAVAILABLE_TITLE}</span>}
      </div>
      <FossabotPrsSortMenu sort={sort} onSortChange={onSortChange} />
    </List.Header>
  );
};
